"use strict";

const TRANSPARENT_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E";

const getScope = (root) => {
  if (root instanceof Element || root instanceof Document) {
    return root;
  }
  return document;
};

const findMediaWrap = (img) => img.closest("[data-deferred-media]");

const setMediaState = (img, state) => {
  const wrap = findMediaWrap(img);
  if (!wrap) {
    return;
  }

  wrap.classList.remove("is-loading");
  wrap.classList.toggle("is-loaded", state === "loaded");
  wrap.classList.toggle("is-error", state === "error");
};

const loadDeferredImage = (img) => {
  if (!(img instanceof HTMLImageElement) || img.dataset.deferredImageLoaded === "1") {
    return;
  }

  const deferredSrc = String(img.getAttribute("data-src") || "").trim();
  const currentSrc = String(img.getAttribute("src") || "").trim();

  if (!deferredSrc && currentSrc && currentSrc !== TRANSPARENT_PLACEHOLDER) {
    if (img.complete && img.naturalWidth > 0) {
      setMediaState(img, "loaded");
    }
    img.dataset.deferredImageLoaded = "1";
    return;
  }

  if (!deferredSrc) {
    setMediaState(img, "error");
    img.dataset.deferredImageLoaded = "1";
    return;
  }

  const onLoad = () => {
    setMediaState(img, "loaded");
  };

  const onError = () => {
    const fallback = String(img.getAttribute("data-fallback-src") || "").trim();
    if (fallback && img.dataset.deferredFallbackApplied !== "1") {
      img.dataset.deferredFallbackApplied = "1";
      img.src = fallback;
      return;
    }
    setMediaState(img, "error");
  };

  img.addEventListener("load", onLoad, { once: true });
  img.addEventListener("error", onError);
  img.dataset.deferredImageLoaded = "1";
  img.src = deferredSrc;
  img.removeAttribute("data-src");
};

export const initDeferredMedia = (root = document) => {
  if (typeof document === "undefined") {
    return;
  }

  const scope = getScope(root);
  const images = Array.from(scope.querySelectorAll("img[data-deferred-image]:not([data-deferred-image-bound])"));
  if (!images.length) {
    return;
  }

  images.forEach((img) => {
    img.setAttribute("data-deferred-image-bound", "1");
  });

  if ("IntersectionObserver" in window) {
    const observers = new Map();

    images.forEach((img) => {
      const rootMargin = String(img.getAttribute("data-deferred-root-margin") || "520px 0px").trim() || "520px 0px";
      let observer = observers.get(rootMargin);

      if (!observer) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            loadDeferredImage(entry.target);
            observer.unobserve(entry.target);
          });
        }, { rootMargin });
        observers.set(rootMargin, observer);
      }

      observer.observe(img);
    });
    return;
  }

  images.forEach(loadDeferredImage);
};

window.initDeferredMedia = initDeferredMedia;
window.initDeferredImages = initDeferredMedia;

const startDeferredMedia = () => {
  requestAnimationFrame(() => initDeferredMedia(document));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startDeferredMedia, { once: true });
} else {
  startDeferredMedia();
}
